import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { X } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import { ShopSidebar } from '../components/shop/ShopSidebar';
import { ShopToolbar } from '../components/shop/ShopToolbar';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const devErr = process.env.NODE_ENV !== 'production' ? console.error.bind(console) : () => {};

export default function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [designers, setDesigners] = useState([]);
  const [favIds, setFavIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [page, setPage] = useState(1);
  const { user } = useAuth();

  const filters = useMemo(() => ({
    search: searchParams.get('search') || '',
    designer: searchParams.get('designer') || '',
    category: searchParams.get('category') || '',
    gender: searchParams.get('gender') || '',
    sort_by: searchParams.get('sort_by') || 'newest',
    min_discount: searchParams.get('min_discount') || '',
  }), [searchParams]);

  const selectedDesigners = filters.designer ? filters.designer.split(',').filter(Boolean) : [];

  const updateFilter = useCallback((key, value) => {
    const p = new URLSearchParams(searchParams);
    if (value) p.set(key, value); else p.delete(key);
    setSearchParams(p);
    setPage(1);
  }, [searchParams, setSearchParams]);

  const clearFilters = useCallback(() => { setSearchParams({}); setPage(1); }, [setSearchParams]);

  const toggleDesigner = useCallback((d) => {
    const updated = selectedDesigners.includes(d)
      ? selectedDesigners.filter(x => x !== d)
      : [...selectedDesigners, d];
    updateFilter('designer', updated.join(','));
  }, [selectedDesigners, updateFilter]);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
    params.set('page', page);
    params.set('limit', 24);
    try {
      const { data } = await axios.get(`${API}/products?${params}`);
      setProducts(data.products);
      setTotal(data.total);
    } catch {
      devErr('[Shop] fetchProducts failed');
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    axios.get(`${API}/products/designers`).then(r => setDesigners(r.data)).catch(() => {});
    if (user) {
      axios.get(`${API}/favorites/ids`, { withCredentials: true }).then(r => setFavIds(r.data)).catch(() => {});
    }
  }, [user]);

  const toggleFav = async (productId) => {
    if (!user) return;
    if (favIds.includes(productId)) {
      await axios.delete(`${API}/favorites/${productId}`, { withCredentials: true });
      setFavIds(ids => ids.filter(id => id !== productId));
    } else {
      await axios.post(`${API}/favorites`, { product_id: productId }, { withCredentials: true });
      setFavIds(ids => [...ids, productId]);
    }
  };

  const activeFilterCount = [
    selectedDesigners.length > 0,
    !!filters.category,
    !!filters.gender,
    !!filters.min_discount,
  ].filter(Boolean).length;

  const totalPages = Math.ceil(total / 24);

  const sidebarProps = { filters, designers, selectedDesigners, activeFilterCount, total, updateFilter, clearFilters, toggleDesigner };

  return (
    <div className="bg-white min-h-screen pt-20">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12 border-b border-[#E5E5E5]">
        <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mb-2 font-work">
          {selectedDesigners.length > 1 ? selectedDesigners.join(' · ') : filters.category || selectedDesigners[0] || 'All Products'}
        </p>
        <div className="flex items-end justify-between">
          <h1 className="font-playfair text-4xl text-[#0A0A0A] tracking-tight">
            {filters.search
              ? `"${filters.search}"`
              : selectedDesigners.length > 1
                ? `${selectedDesigners.length} Designers`
                : (selectedDesigners[0] || filters.category || 'The Collection')}
          </h1>
          <p className="text-sm text-[#999999] font-work hidden sm:block">{total} pieces</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
        <ShopToolbar filters={filters} activeFilterCount={activeFilterCount} filtersOpen={filtersOpen} setFiltersOpen={setFiltersOpen} updateFilter={updateFilter} />

        <div className="flex gap-12">
          {/* Desktop Sidebar */}
          <div className="hidden lg:block">
            <ShopSidebar {...sidebarProps} />
          </div>

          {/* Mobile Filters Overlay */}
          {filtersOpen && (
            <div className="fixed inset-0 z-50 bg-white overflow-y-auto lg:hidden p-6">
              <div className="flex items-center justify-between mb-8">
                <p className="font-playfair text-xl">Filters</p>
                <button onClick={() => setFiltersOpen(false)}><X size={20} /></button>
              </div>
              <ShopSidebar {...sidebarProps} />
              <button
                onClick={() => setFiltersOpen(false)}
                className="w-full bg-[#0A0A0A] text-white py-4 text-xs uppercase tracking-widest font-work mt-4"
              >
                View {total} Results
              </button>
            </div>
          )}

          {/* Products Grid */}
          <div className="flex-1">
            {/* Active brand chips */}
            {selectedDesigners.length > 0 && (
              <div className="flex items-center gap-2 mb-6 flex-wrap" data-testid="active-brand-chips">
                {selectedDesigners.map(d => (
                  <button
                    key={d}
                    onClick={() => toggleDesigner(d)}
                    className="flex items-center gap-1.5 bg-[#0A0A0A] text-white px-3 py-1.5 text-xs font-work hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-colors"
                    data-testid={`brand-chip-${d.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    {d} <X size={9} />
                  </button>
                ))}
                {selectedDesigners.length > 1 && (
                  <button
                    onClick={() => updateFilter('designer', '')}
                    className="text-xs text-[#999999] font-work hover:text-[#D4AF37] transition-colors underline underline-offset-2"
                    data-testid="clear-all-brands"
                  >
                    Clear all brands
                  </button>
                )}
              </div>
            )}

            {loading ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6 gap-y-12">
                {[...Array(8)].map((_, i) => (
                  <div key={`skeleton-${i}`} className="flex flex-col gap-3">
                    <div className="aspect-[4/5] bg-[#F3F3F3] animate-pulse" />
                    <div className="h-3 bg-[#F3F3F3] animate-pulse w-1/2" />
                    <div className="h-4 bg-[#F3F3F3] animate-pulse w-3/4" />
                  </div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-24">
                <p className="font-playfair text-2xl text-[#0A0A0A] mb-4">No products found</p>
                <p className="text-[#999999] text-sm font-work mb-8">Try adjusting your filters or search query</p>
                <button onClick={clearFilters} className="border border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition-colors px-8 py-3 text-xs uppercase tracking-widest font-work">
                  Clear Filters
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-6 gap-y-12" data-testid="products-grid">
                  {products.map(p => (
                    <ProductCard
                      key={p.product_id}
                      product={p}
                      isFav={favIds.includes(p.product_id)}
                      onToggleFav={() => toggleFav(p.product_id)}
                    />
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-16">
                    {[...Array(totalPages)].map((_, i) => (
                      <button
                        key={`page-${i + 1}`}
                        data-testid={`shop-page-${i + 1}`}
                        onClick={() => setPage(i + 1)}
                        className={`w-9 h-9 text-sm font-work transition-colors ${page === i + 1 ? 'bg-[#0A0A0A] text-white' : 'border border-[#E5E5E5] text-[#0A0A0A] hover:border-[#D4AF37] hover:text-[#D4AF37]'}`}
                      >
                        {i + 1}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
